/**
 * @file Post-reload reconciliation between persisted sessions and Uppy/GoldenRetriever state.
 * Merges restored files into sessions, handles orphans, and resumes restorable uploads.
 */

import { UPLOAD_IN_PROGRESS_STATUSES } from './types';
import {
  getUploadKindAdapter,
  getXhrAllowedMetaFields,
} from './upload-kind-registry';
import { isMultipartContextExpired } from './upload-manager-reducer';

import type {
  PersistedMultipartContext,
  UploadFileState,
  UploadFileStatus,
  UploadResourceSession,
} from './types';

/** User-facing error when GoldenRetriever could not restore file blobs. */
export const UPLOAD_INTERRUPTED_ERROR_MESSAGE = 'Upload interrupted';

/** User-facing error when S3 multipart registration exceeded presigned URL TTL. */
export const UPLOAD_EXPIRED_ERROR_MESSAGE = 'Upload session expired';

/** User-facing error when upload progress stalls beyond {@link UPLOAD_STALL_TIMEOUT_MS}. */
export const UPLOAD_STALLED_ERROR_MESSAGE = 'Upload stalled';

/** Minimal Uppy file shape used during hydration (avoids tight Uppy generic coupling). */
export type HydrateUppyFile = {
  id: string;
  name?: string;
  size?: number | null;
  data?: Blob | File | null;
  isPaused?: boolean;
  isGhost?: boolean;
  progress?: {
    percentage?: number;
    /** Uppy uses a timestamp when started, `null` when not started. */
    uploadStarted?: number | boolean | null;
    uploadComplete?: boolean;
  };
  meta?: Record<string, unknown>;
};

/** Subset of Uppy API used by hydration and recovery helpers. */
export type ResumableUppy = {
  getState: () => {
    currentUploads?: Record<string, unknown>;
    recoveredState?: unknown;
  };
  getFiles: () => HydrateUppyFile[];
  on: (event: 'restored' | 'complete', handler: () => void) => void;
  off: (event: 'restored' | 'complete', handler: () => void) => void;
  emit: (event: 'restore-confirmed') => void;
  upload: () => Promise<unknown>;
};

/** Outcome of waiting for GoldenRetriever blob restore after metadata recovery. */
export type GoldenRetrieverRecoveryResult =
  | 'no-recovery'
  | 'restored'
  | 'timeout';

const buildFileStateFromUppyFile = (
  file: HydrateUppyFile,
  persistedContext: PersistedMultipartContext | undefined
): UploadFileState => {
  const expired =
    persistedContext != null &&
    isMultipartContextExpired(persistedContext.registeredAt);

  if (file.isGhost) {
    return {
      uppyFileId: file.id,
      fileName: file.name ?? 'Unknown file',
      bytesTotal: file.size ?? 0,
      progress: file.progress?.percentage ?? 0,
      status: 'error',
      errorMessage: UPLOAD_INTERRUPTED_ERROR_MESSAGE,
    };
  }

  if (expired) {
    return {
      uppyFileId: file.id,
      fileName: file.name ?? 'Unknown file',
      bytesTotal: file.size ?? 0,
      progress: file.progress?.percentage ?? 0,
      status: 'error',
      errorMessage: UPLOAD_EXPIRED_ERROR_MESSAGE,
    };
  }

  let status: UploadFileStatus = 'queued';
  if (file.progress?.uploadComplete) {
    status = 'complete';
  } else if (file.isPaused) {
    status = 'paused';
  } else if (file.progress?.uploadStarted) {
    status = 'uploading';
  }

  return {
    uppyFileId: file.id,
    fileName: file.name ?? 'Unknown file',
    bytesTotal: file.size ?? 0,
    progress: file.progress?.percentage ?? 0,
    status,
  };
};

const UPPY_FILE_ENVELOPE_META_KEYS = new Set([
  'uploadKind',
  'resourceKey',
  'returnPath',
  'sessionLabel',
]);

const getXhrPayloadMetaKeys = (): Set<string> =>
  new Set(getXhrAllowedMetaFields());

const extractSessionMetaFromUppyFile = (
  file: HydrateUppyFile
): Record<string, unknown> => {
  const meta: Record<string, unknown> = {};

  if (!file.meta) {
    return meta;
  }

  const xhrPayloadMetaKeys = getXhrPayloadMetaKeys();

  for (const [key, value] of Object.entries(file.meta)) {
    if (UPPY_FILE_ENVELOPE_META_KEYS.has(key) || xhrPayloadMetaKeys.has(key)) {
      continue;
    }
    meta[key] = value;
  }

  const uploadKind =
    typeof file.meta.uploadKind === 'string' ? file.meta.uploadKind : undefined;
  const adapterMeta = uploadKind
    ? (getUploadKindAdapter(uploadKind)?.extractHydratedSessionMeta?.(
        file.meta
      ) ?? {})
    : {};

  return { ...meta, ...adapterMeta };
};

const upsertFileInSession = (
  session: UploadResourceSession,
  fileState: UploadFileState
): UploadResourceSession => {
  const fileIndex = session.files.findIndex(
    (item) => item.uppyFileId === fileState.uppyFileId
  );
  const files =
    fileIndex === -1
      ? [...session.files, fileState]
      : session.files.map((item, index) =>
          index === fileIndex ? fileState : item
        );

  return {
    ...session,
    files,
    dismissed: false,
    completedAt: undefined,
  };
};

/**
 * Merges Uppy-restored files into persisted session map, creating sessions when missing.
 */
export const mergeUppyFilesIntoSessions = (
  sessionsMap: Map<string, UploadResourceSession>,
  restoredFiles: HydrateUppyFile[],
  persistedContexts: Map<string, PersistedMultipartContext>
): Map<string, UploadResourceSession> => {
  const next = new Map(sessionsMap);

  restoredFiles.forEach((file) => {
    const resourceKey =
      typeof file.meta?.resourceKey === 'string' ? file.meta.resourceKey : null;
    if (!resourceKey) {
      return;
    }

    const fileState = buildFileStateFromUppyFile(
      file,
      persistedContexts.get(file.id)
    );

    const existing = next.get(resourceKey);
    if (existing) {
      next.set(resourceKey, upsertFileInSession(existing, fileState));
      return;
    }

    next.set(resourceKey, {
      id: resourceKey,
      kind: String(file.meta?.uploadKind ?? 'unknown'),
      label: String(file.meta?.sessionLabel ?? 'Upload'),
      returnPath: String(file.meta?.returnPath ?? '/'),
      meta: extractSessionMetaFromUppyFile(file),
      files: [fileState],
      dismissed: false,
    });
  });

  return next;
};

/**
 * Marks in-progress files missing from Uppy after reload as interrupted errors.
 */
export const reconcileOrphanedSessions = (
  sessionsMap: Map<string, UploadResourceSession>,
  restoredUppyFileIds: Set<string>
): Map<string, UploadResourceSession> => {
  const next = new Map<string, UploadResourceSession>();

  sessionsMap.forEach((session, sessionId) => {
    const files = session.files.map((file) => {
      const isInProgress = UPLOAD_IN_PROGRESS_STATUSES.includes(file.status);
      const isMissingFromUppy = !restoredUppyFileIds.has(file.uppyFileId);

      if (isInProgress && isMissingFromUppy) {
        return {
          ...file,
          status: 'error' as const,
          errorMessage: UPLOAD_INTERRUPTED_ERROR_MESSAGE,
        };
      }

      return file;
    });

    next.set(sessionId, { ...session, files });
  });

  return next;
};

/** Marks in-progress session rows present in Uppy but missing restorable blob/context. */
export const markNonRestorableSessionFiles = (
  sessionsMap: Map<string, UploadResourceSession>,
  restoredFiles: HydrateUppyFile[],
  persistedContexts: Map<string, PersistedMultipartContext>
): Map<string, UploadResourceSession> => {
  const restorableIds = new Set(
    getRestorableUppyFileIds(restoredFiles, persistedContexts)
  );
  const restoredFileIds = new Set(restoredFiles.map((file) => file.id));
  const next = new Map<string, UploadResourceSession>();

  sessionsMap.forEach((session, sessionId) => {
    const files = session.files.map((file) => {
      if (
        UPLOAD_IN_PROGRESS_STATUSES.includes(file.status) &&
        restoredFileIds.has(file.uppyFileId) &&
        !restorableIds.has(file.uppyFileId)
      ) {
        return {
          ...file,
          status: 'error' as const,
          errorMessage: UPLOAD_INTERRUPTED_ERROR_MESSAGE,
        };
      }

      return file;
    });

    next.set(sessionId, { ...session, files });
  });

  return next;
};

/**
 * Returns Uppy file ids that have restorable blobs and non-expired multipart context.
 */
export const getRestorableUppyFileIds = (
  restoredFiles: HydrateUppyFile[],
  persistedContexts: Map<string, PersistedMultipartContext>
): string[] =>
  restoredFiles
    .filter((file) => {
      if (file.isGhost || file.progress?.uploadComplete) {
        return false;
      }

      if (file.data == null) {
        return false;
      }

      const context = persistedContexts.get(file.id);
      if (!context) {
        return true;
      }

      return !isMultipartContextExpired(context.registeredAt);
    })
    .map((file) => file.id);

const blobRestoreComplete = (uppy: ResumableUppy): boolean => {
  const files = uppy.getFiles();

  if (uppy.getState().recoveredState != null && files.length === 0) {
    return false;
  }

  if (files.length === 0) {
    return true;
  }

  return files.every((file) => file.data != null);
};

/** Options for {@link reconcileHydratedSessions}. */
export type ReconcileHydratedSessionsOptions = {
  /** When true, mark in-progress files without restorable blobs as interrupted. */
  markNonRestorable?: boolean;
};

/**
 * Merges persisted sessions with Uppy-restored files and optionally marks orphans
 * or non-restorable in-progress files as interrupted after recovery timeout.
 */
export const reconcileHydratedSessions = (
  persistedSessions: UploadResourceSession[],
  restoredFiles: HydrateUppyFile[],
  persistedContexts: Map<string, PersistedMultipartContext>,
  options?: ReconcileHydratedSessionsOptions
): Map<string, UploadResourceSession> => {
  const restoredUppyFileIds = new Set(restoredFiles.map((file) => file.id));

  let sessionsMap = new Map<string, UploadResourceSession>();
  for (const session of persistedSessions) {
    sessionsMap.set(session.id, {
      ...session,
      files: [...session.files],
    });
  }

  sessionsMap = mergeUppyFilesIntoSessions(
    sessionsMap,
    restoredFiles,
    persistedContexts
  );

  const shouldReconcileOrphans =
    options?.markNonRestorable ||
    (restoredFiles.length === 0 &&
      persistedSessions.some((session) =>
        session.files.some((file) =>
          UPLOAD_IN_PROGRESS_STATUSES.includes(file.status)
        )
      ));

  if (shouldReconcileOrphans) {
    sessionsMap = reconcileOrphanedSessions(sessionsMap, restoredUppyFileIds);
  }

  if (options?.markNonRestorable) {
    sessionsMap = markNonRestorableSessionFiles(
      sessionsMap,
      restoredFiles,
      persistedContexts
    );
  }

  return sessionsMap;
};

/** Wait for GoldenRetriever to finish async blob restore after metadata recovery. */
export const waitForGoldenRetrieverRestore = (
  uppy: ResumableUppy,
  timeoutMs: number
): Promise<GoldenRetrieverRecoveryResult> => {
  if (uppy.getState().recoveredState == null) {
    return Promise.resolve('no-recovery');
  }

  if (blobRestoreComplete(uppy)) {
    return Promise.resolve('restored');
  }

  return new Promise((resolve) => {
    let settled = false;

    const finish = (result: GoldenRetrieverRecoveryResult) => {
      if (settled) {
        return;
      }
      settled = true;
      uppy.off('restored', onRestored);
      clearTimeout(timer);
      resolve(result);
    };

    const onRestored = () => finish('restored');
    uppy.on('restored', onRestored);

    const timer = setTimeout(() => finish('timeout'), timeoutMs);
  });
};

/** Options for {@link tryUploadPendingRestorable}. */
export type TryUploadPendingRestorableOptions = {
  /** Skip the `currentUploads` guard — used after reload when batches are phantom. */
  force?: boolean;
};

/**
 * Starts Uppy upload when restorable pending files exist and no active batches block resume.
 */
export const tryUploadPendingRestorable = (
  uppy: ResumableUppy,
  persistedContexts: Map<string, PersistedMultipartContext>,
  options?: TryUploadPendingRestorableOptions
): void => {
  const hasActiveBatches =
    Object.keys(uppy.getState().currentUploads ?? {}).length > 0;
  if (hasActiveBatches && !options?.force) {
    return;
  }

  const pendingIds = getRestorableUppyFileIds(
    uppy.getFiles(),
    persistedContexts
  );
  if (pendingIds.length === 0) {
    return;
  }

  void uppy.upload();
};

/**
 * Resume uploads after GoldenRetriever restore.
 * After reload, restored `currentUploads` batches are phantom — resume immediately.
 */
export const resumeRestoredUploads = (
  uppy: ResumableUppy,
  persistedContexts: Map<string, PersistedMultipartContext>
): (() => void) => {
  uppy.emit('restore-confirmed');

  const hasActiveBatches =
    Object.keys(uppy.getState().currentUploads ?? {}).length > 0;

  if (!hasActiveBatches) {
    tryUploadPendingRestorable(uppy, persistedContexts);
    return () => undefined;
  }

  // Phantom batches from reload — do not wait for a `complete` that will never fire.
  tryUploadPendingRestorable(uppy, persistedContexts, { force: true });

  const onComplete = () => {
    tryUploadPendingRestorable(uppy, persistedContexts);

    const stillActiveBatches =
      Object.keys(uppy.getState().currentUploads ?? {}).length > 0;
    const pendingIds = getRestorableUppyFileIds(
      uppy.getFiles(),
      persistedContexts
    );

    if (!stillActiveBatches && pendingIds.length === 0) {
      uppy.off('complete', onComplete);
    }
  };

  uppy.on('complete', onComplete);

  return () => {
    uppy.off('complete', onComplete);
  };
};
